import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { API_ROUTES, apiFetch } from '@/config/api-routes'
import { PAPEL_LABEL } from '@/lib/types'
import { useAuth } from '@/contexts/AuthContext'

export default function AdminWorkspaceDetailPage() {
  const { id: workspaceId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enterWorkspaceAsAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [workspace, setWorkspace] = useState<Record<string, unknown> | null>(null)
  const [membros, setMembros] = useState<Record<string, unknown>[]>([])
  const [status, setStatus] = useState('ativo')

  useEffect(() => {
    if (!workspaceId) return
    apiFetch(`${API_ROUTES.admin.workspaces}/${workspaceId}`)
      .then(r => r.json())
      .then(j => {
        if (j.error) throw new Error(j.error)
        setWorkspace(j.workspace)
        setMembros(j.membros ?? [])
        setStatus(String(j.workspace?.status ?? 'ativo'))
      })
      .catch(e => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setLoading(false))
  }, [workspaceId])

  async function salvarStatus() {
    const statusAtual = String(workspace?.status ?? 'ativo')
    if (status === statusAtual) return
    if (!window.confirm(`Alterar o status do workspace para "${status}"? Isso pode afetar o acesso da equipe.`)) return
    setBusy(true)
    setErro(null)
    try {
      const r = await apiFetch(`${API_ROUTES.admin.workspaces}/${workspaceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error ?? 'Falha')
      setWorkspace(prev => prev ? { ...prev, status: j.workspace.status } : prev)
      navigate('/admin/medicos')
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setBusy(false)
    }
  }

  async function abrir() {
    if (!workspaceId) return
    setBusy(true)
    try {
      await enterWorkspaceAsAdmin(workspaceId)
    } catch {
      setErro('Não foi possível abrir o workspace.')
      setBusy(false)
    }
  }

  const titular = workspace?.titular as { nome?: string; email?: string; crm?: string } | undefined

  if (!workspaceId) return null

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <Link to="/admin/medicos" style={{ fontSize: 13, color: 'var(--primary-deep)', fontWeight: 600 }}>← Voltar</Link>
        <h1 className="page-title" style={{ marginTop: 8 }}>{String(workspace?.nome ?? 'Workspace')}</h1>
        {titular && (
          <p className="page-subtitle" style={{ margin: 0 }}>
            {titular.nome} · {titular.email}{titular.crm ? ` · CRM ${titular.crm}` : ''}
          </p>
        )}
      </header>

      {loading ? (
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', color: 'var(--muted)' }} aria-hidden />
      ) : (
        <>
          {erro && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

          <div className="card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
            <div>
              <label className="field-label" htmlFor="ws-status">Status do workspace</label>
              <select id="ws-status" className="field-input" value={status} onChange={e => setStatus(e.target.value)}>
                <option value="ativo">Ativo</option>
                <option value="suspenso">Suspenso</option>
                <option value="encerrado">Encerrado</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn-primary" disabled={busy} onClick={salvarStatus}>Salvar status</button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                title="Você passará a navegar o app como este workspace, saindo do painel admin"
                onClick={abrir}
              >
                Entrar como este workspace
              </button>
            </div>
          </div>

          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Membros</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {membros.map(m => {
              const perfilRaw = m.perfis_usuario
              const perfil = (Array.isArray(perfilRaw) ? perfilRaw[0] : perfilRaw) as { nome?: string; email?: string } | null
              return (
                <div key={String(m.id)} className="card" style={{ padding: '12px 14px', fontSize: 14 }}>
                  <strong>{PAPEL_LABEL[m.papel as keyof typeof PAPEL_LABEL] ?? String(m.papel)}</strong>
                  {' · '}
                  {String(perfil?.nome ?? m.convite_email ?? '—')}
                  {!m.ativo && ' · inativo'}
                  {m.convite_status === 'pendente' && ' · convite pendente'}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
